import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const Logo = ({ white = false, className = "h-7" }) => (
  <img src="/logo-white.png" alt="RP Home" className={`${white ? "" : "invert"} w-auto ${className}`} />
);

export const Chip = ({ active, children, className = "", ...props }) => (
  <button
    type="button"
    className={`rp-chip ${active ? "bg-[#070707] text-white" : "bg-[#EBEBEB] text-black hover:bg-[#E6DDCE]"} ${className}`}
    {...props}
  >
    {children}
  </button>
);

const VARIANTS = {
  primary: "bg-[#070707] text-white hover:bg-black/80",
  secondary: "bg-[#EBEBEB] text-black hover:bg-[#E6DDCE]",
  outline: "border border-[#EBEBEB] bg-white text-black hover:bg-[#F7F7F7]",
  danger: "bg-white border border-[#DC3545]/40 text-[#DC3545] hover:bg-[#DC3545]/5",
  success: "bg-[#198754] text-white hover:bg-[#198754]/90",
};

export const Btn = ({ variant = "primary", loading, children, className = "", ...props }) => (
  <button className={`rp-btn ${VARIANTS[variant]} ${className}`} disabled={loading || props.disabled} {...props}>
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </button>
);

const TONES = {
  success: "bg-[#198754]/10 text-[#198754]",
  warning: "bg-[#FFC107]/20 text-[#8a6500]",
  error: "bg-[#DC3545]/10 text-[#DC3545]",
  sand: "bg-[#E6DDCE] text-black",
  muted: "bg-[#EBEBEB] text-[#757575]",
};

export const Pill = ({ tone = "muted", children, ...props }) => (
  <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${TONES[tone]}`} {...props}>
    {children}
  </span>
);

export const ProgressBar = ({ value, dark = false, ...props }) => (
  <div className={`h-2 w-full rounded-full overflow-hidden ${dark ? "bg-white/25" : "bg-[#EBEBEB]"}`} {...props}>
    <div
      className={`h-full rounded-full transition-[width] duration-700 ease-out ${dark ? "bg-white" : "bg-[#070707]"}`}
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-2">
    <span className="rp-label">{label}</span>
    {children}
    {hint && <span className="block text-xs text-[#757575]">{hint}</span>}
  </label>
);

export const Spinner = () => (
  <div className="flex justify-center py-20" data-testid="loading-spinner">
    <Loader2 className="h-6 w-6 animate-spin text-[#757575]" />
  </div>
);

export const Empty = ({ icon: Icon, title, text, children, ...props }) => (
  <div className="rp-card p-10 text-center flex flex-col items-center gap-3" {...props}>
    {Icon && <Icon className="h-7 w-7 text-[#C8BDAB]" strokeWidth={1.5} />}
    <p className="font-medium">{title}</p>
    {text && <p className="text-sm text-[#757575] max-w-sm">{text}</p>}
    {children}
  </div>
);

export const PageHeader = ({ label, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8 rp-fade">
    <div>
      {label && <p className="rp-label mb-2">{label}</p>}
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Modal = ({ open, onOpenChange, title, description, children, testid, wide }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid={testid}
      className={`bg-white rounded-[20px] sm:rounded-[20px] border-[#EBEBEB] p-6 sm:p-8 max-h-[92vh] overflow-y-auto w-[calc(100%-24px)] ${wide ? "max-w-2xl" : "max-w-lg"}`}
    >
      <DialogHeader className="text-left">
        <DialogTitle className="text-xl font-semibold tracking-tight">{title}</DialogTitle>
        {description ? <DialogDescription className="text-[#757575]">{description}</DialogDescription> : <DialogDescription className="sr-only">{title}</DialogDescription>}
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);
